<script setup lang="ts">
import useCityStore from '~~/app/store/city'
import useUserStore from '~~/app/store/user'
import { useI18n } from 'vue-i18n'
import {
    getAppStoreChargemapUrl,
    getChargemapUrl,
    getPlayStoreChargemapUrl
} from '~~/app/utils/url'
import { useBreakpoints } from '@vueuse/core'
import Networks from '~~/app/components/Cities/Networks.vue'
import Attendance from '~~/app/components/Common/Attendance.vue'

const cityStore = useCityStore()
const userStore = useUserStore()
const { isApple } = useDevice()
const breakpoints = useBreakpoints({ tablet: 768 })
const isMobile = breakpoints.isSmallerOrEqual('tablet')
const { t } = useI18n()

const userId = useUserSession().user?.value?.id

const attendanceCtaLabel = computed<string>(() =>
    isMobile ? t('action.download_app') : t('word.order_your_pass')
)

const appStoreUrl = computed<string>(() =>
    isApple ? getAppStoreChargemapUrl() : getPlayStoreChargemapUrl()
)

const attendanceCtaUrl = computed<string>(() =>
    isMobile ? appStoreUrl.value : getChargemapUrl('/pass/purchase/step1')
)

const displayNetworks = computed<boolean>(() => !!cityStore.stationsTotalCount)
const displayAttendance = computed<boolean>(() => cityStore.checkinsCount >= 10)
</script>

<template>
    <div
        class="w-full py-12 bg-white md:bg-sky-100 flex-col justify-center items-center md:gap-7 inline-flex md:mt-12"
    >
        <i18n-t keypath="cities.data.title" tag="h2" scope="global">
            <template #city>
                <span class="text-sky-500">
                    {{ cityStore.name }}
                </span>
            </template>
        </i18n-t>
        <div
            class="flex flex-col md:grid gap-3 md:gap-7 md:mx-14"
            :class="
                displayNetworks && displayAttendance
                    ? 'grid-cols-2'
                    : 'grid-cols-1'
            "
        >
            <Networks v-if="displayNetworks" />
            <Attendance
                v-if="displayAttendance"
                class="!mt-1 !md:gap-2"
                :checkins-count="cityStore.checkinsCount"
                :change-order="false"
            />
            <NuxtLink
                v-if="displayNetworks && !userId && !isMobile"
                class="m-auto"
                :to="getChargemapUrl('/user/index')"
                :title="$t('join_chargemap.signup')"
            >
                <ChargemapB2CButton type="primary">
                    <IconUserCircle class="size-7" is-solid />
                    {{ $t('join_chargemap.signup') }}
                </ChargemapB2CButton>
            </NuxtLink>
            <NuxtLink
                v-if="
                    displayAttendance &&
                    (isMobile || !userStore.userAlreadyHasAPass)
                "
                class="m-auto"
                :to="attendanceCtaUrl"
                :title="attendanceCtaLabel"
            >
                <ChargemapB2CButton type="primary">
                    <IconCaddie v-if="!isMobile" class="size-7" />
                    {{ attendanceCtaLabel }}
                </ChargemapB2CButton>
            </NuxtLink>
        </div>
    </div>
</template>

<style lang="scss" scoped>
h2 {
    @apply text-center text-2xl md:text-4xl leading-normal md:leading-10 mx-4;
}
</style>
